"use client";

import { useEffect, useRef } from "react";
import { buildAuthHeaders } from "@/utils/auth-headers";
import StripeRepository from "@/repositories/stripe/stripe-repository";

export type AbandonReason = "drawer_close" | "tab_close" | "offline" | "startup_retry";

export interface PendingAbandonment {
  piId: string;
  reason: AbandonReason;
}

const ABANDONMENT_STORAGE_KEY = "ev_abandoned_payment_intents";

function isAbandonReason(value: unknown): value is AbandonReason {
  return (
    value === "drawer_close" ||
    value === "tab_close" ||
    value === "offline" ||
    value === "startup_retry"
  );
}

function normalizePendingAbandonments(value: unknown): PendingAbandonment[] {
  if (!Array.isArray(value)) {
    return [];
  }

  const normalized: PendingAbandonment[] = [];
  const seenPiIds = new Set<string>();

  for (const item of value) {
    let piId: string | null = null;
    let reason: AbandonReason = "startup_retry";

    if (typeof item === "string") {
      piId = item;
    } else if (item && typeof item === "object") {
      const record = item as { piId?: unknown; reason?: unknown };

      if (typeof record.piId === "string") {
        piId = record.piId;
      }

      if (isAbandonReason(record.reason)) {
        reason = record.reason;
      }
    }

    if (!piId || seenPiIds.has(piId)) {
      continue;
    }

    seenPiIds.add(piId);
    normalized.push({ piId, reason });
  }

  return normalized;
}

function persistPendingAbandonments(entries: PendingAbandonment[]): void {
  localStorage.setItem(ABANDONMENT_STORAGE_KEY, JSON.stringify(entries));
}

export function getPendingAbandonments(): PendingAbandonment[] {
  if (typeof window === "undefined") { return []; }
  try {
    const stored = localStorage.getItem(ABANDONMENT_STORAGE_KEY);
    const parsed = JSON.parse(stored || "[]");
    return normalizePendingAbandonments(parsed);
  } catch {
    return [];
  }
}

export function addPendingAbandonment(piId: string, reason: AbandonReason): void {
  if (typeof window === "undefined") { return; }
  try {
    const current = getPendingAbandonments();
    if (current.some((entry) => entry.piId === piId)) {
      return;
    }

    persistPendingAbandonments([...current, { piId, reason }]);
  } catch { /* best effort */ }
}

export function removePendingAbandonment(piId: string): void {
  if (typeof window === "undefined") { return; }
  try {
    const current = getPendingAbandonments();
    persistPendingAbandonments(current.filter((entry) => entry.piId !== piId));
  } catch { /* best effort */ }
}

export async function sendAbandonmentNormal(piId: string, reason: AbandonReason): Promise<boolean> {
  try {
    await StripeRepository.getInstance().abandonOneTimePayment(piId, reason);
    return true;
  } catch {
    return false;
  }
}

export function sendAbandonmentKeepalive(piId: string, reason: AbandonReason): void {
  try {
    const headers = buildAuthHeaders();
    if (!headers) { return; }

    const apiUrl = process.env.NEXT_PUBLIC_API_URL || "";
    void fetch(`${apiUrl}/payments/one-time-payment/abandon`, {
      method: "POST",
      headers,
      body: JSON.stringify({ paymentIntentId: piId, reason }),
      keepalive: true,
    }).catch(() => undefined);
  } catch {
    return;
  }
}

interface UsePaymentIntentAbandonmentParams {
  paymentIntentId: string | null;
  paymentSucceeded: boolean;
}

export function usePaymentIntentAbandonment({
  paymentIntentId,
  paymentSucceeded,
}: UsePaymentIntentAbandonmentParams): void {
  // Ref to avoid stale closures in beforeunload / offline handlers
  const eligiblePiIdRef = useRef<string | null>(null);

  // Keep ref in sync with current eligible PI id
  useEffect(() => {
    const isEligible =
      typeof paymentIntentId === "string" &&
      paymentIntentId.startsWith("pi_") &&
      !paymentSucceeded;

    eligiblePiIdRef.current = isEligible ? paymentIntentId : null;
  }, [paymentIntentId, paymentSucceeded]);

  // beforeunload: keepalive fetch + localStorage backup
  useEffect(() => {
    const handleBeforeUnload = () => {
      const piId = eligiblePiIdRef.current;
      if (!piId) { return; }
      sendAbandonmentKeepalive(piId, "tab_close");
      addPendingAbandonment(piId, "tab_close"); // backup in case keepalive fails
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, []); // intentionally empty — reads only from ref

  // offline: save to localStorage for retry on reconnect
  useEffect(() => {
    const handleOffline = () => {
      const piId = eligiblePiIdRef.current;
      if (!piId) { return; }
      addPendingAbandonment(piId, "offline");
    };

    window.addEventListener("offline", handleOffline);
    return () => window.removeEventListener("offline", handleOffline);
  }, []); // intentionally empty — reads only from ref

  // cleanup on unmount: remove from localStorage if drawer-close already sent the signal
  useEffect(() => {
    return () => {
      const piId = eligiblePiIdRef.current;
      if (piId) {
        removePendingAbandonment(piId);
      }
    };
  }, []); // intentionally empty — reads only from ref
}
